import { useState, useEffect, useCallback } from 'react';
import { useTranslation as useI18n } from 'react-i18next';
import { translationAPI } from '../lib/api';

// In-memory cache for translations
const translationCache = {};

export const useContentTranslation = () => {
  const { i18n } = useI18n();
  // Normalize language code: 'en-US' -> 'en', 'en-US@posix' -> 'en'
  const rawLang = i18n.language || 'en';
  const currentLang = rawLang.split('-')[0].split('@')[0].toLowerCase();
  const normalizedLang = ['en', 'es'].includes(currentLang) ? currentLang : 'en';
  const [isTranslating, setIsTranslating] = useState(false);

  const getCacheKey = (text, lang) => `${text}:${lang}`;

  const translateTexts = useCallback(async (texts, targetLang = normalizedLang) => {
    if (!texts || texts.length === 0) return texts;
    
    // Check which texts need translation
    const results = [];
    const textsToTranslate = [];
    const indicesToTranslate = [];

    texts.forEach((text, index) => {
      if (!text || text.trim() === '') {
        results[index] = text;
        return;
      }

      const cacheKey = getCacheKey(text, targetLang);
      if (translationCache[cacheKey]) {
        results[index] = translationCache[cacheKey];
      } else {
        results[index] = null; // Placeholder
        textsToTranslate.push(text);
        indicesToTranslate.push(index);
      }
    });

    // If all translations are cached, return immediately
    if (textsToTranslate.length === 0) {
      return results;
    }

    // Translate uncached texts
    try {
      setIsTranslating(true);
      const response = await translationAPI.translate(textsToTranslate, targetLang);
      const translations = response.data.translations;

      // Map translations back and cache them
      indicesToTranslate.forEach((idx, j) => {
        const translated = translations[j] || textsToTranslate[j];
        results[idx] = translated;
        const cacheKey = getCacheKey(textsToTranslate[j], targetLang);
        translationCache[cacheKey] = translated;
      });
    } catch (error) {
      console.error('Translation error:', error);
      // Fallback to original texts on error
      indicesToTranslate.forEach((idx, j) => {
        results[idx] = textsToTranslate[j];
      });
    } finally {
      setIsTranslating(false);
    }

    return results;
  }, [normalizedLang]);

  const translateSingle = useCallback(async (text, targetLang = normalizedLang) => {
    if (!text) return text;
    const results = await translateTexts([text], targetLang);
    return results[0];
  }, [translateTexts, normalizedLang]);

  return {
    translateTexts,
    translateSingle,
    isTranslating,
    currentLang: normalizedLang,
  };
};

// Hook for a translatable text field with show original toggle
export const useTranslatableField = (originalText, autoTranslate = true) => {
  const { i18n } = useI18n();
  // Normalize language code: 'en-US' -> 'en', 'en-US@posix' -> 'en'
  const rawLang = i18n.language || 'en';
  const currentLang = rawLang.split('-')[0].split('@')[0].toLowerCase();
  const normalizedLang = ['en', 'es'].includes(currentLang) ? currentLang : 'en';
  
  const [translatedText, setTranslatedText] = useState(originalText);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [hasTranslation, setHasTranslation] = useState(false);

  useEffect(() => {
    if (!originalText || !autoTranslate) {
      setTranslatedText(originalText);
      setHasTranslation(false);
      return;
    }

    const cacheKey = `${originalText}:${normalizedLang}`;
    if (translationCache[cacheKey]) {
      setTranslatedText(translationCache[cacheKey]);
      setHasTranslation(translationCache[cacheKey] !== originalText);
      return;
    }

    // Translate
    const doTranslate = async () => {
      setIsTranslating(true);
      try {
        const response = await translationAPI.translate([originalText], normalizedLang);
        const translated = response.data.translations[0];
        translationCache[cacheKey] = translated;
        setTranslatedText(translated);
        setHasTranslation(translated !== originalText);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedText(originalText);
        setHasTranslation(false);
      } finally {
        setIsTranslating(false);
      }
    };

    doTranslate();
  }, [originalText, normalizedLang, autoTranslate]);

  const displayText = showOriginal ? originalText : translatedText;
  const toggleOriginal = () => setShowOriginal(!showOriginal);

  return {
    displayText,
    originalText,
    translatedText,
    showOriginal,
    toggleOriginal,
    isTranslating,
    hasTranslation,
  };
};

export default useContentTranslation;
